import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, User, CreditCard, Briefcase, Gift, Users, Menu, X, LogOut, Calendar, Bell, ChevronDown, Settings, FileText, Award, DollarSign, BarChart, BookOpen } from 'lucide-react';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    logout
  } = useAuth();
  const getMenuItems = () => {
    const role = user?.role;
    // SUPER ADMIN - Full access
    if (role === 'super_admin') {
      return [{
        name: 'Dasbor',
        icon: LayoutDashboard,
        path: '/admin/dashboard'
      }, {
        name: 'Anggota',
        icon: Users,
        path: '/admin/members'
      }, {
        name: 'Analitik',
        icon: BarChart,
        path: '/admin/analytics'
      }, {
        name: 'Pengaturan',
        icon: Settings,
        path: '/admin/settings'
      }];
    }
    // KETUA CHAPTER - Chapter management
    if (role === 'ketua_chapter' || role === 'ketua_student_chapter') {
      return [{
        name: 'Dasbor',
        icon: LayoutDashboard,
        path: '/chapter/dashboard'
      }, {
        name: 'Anggota',
        icon: Users,
        path: '/chapter/members'
      }, {
        name: 'Acara',
        icon: Calendar,
        path: '/events'
      }, {
        name: 'Laporan',
        icon: FileText,
        path: '/chapter/reports'
      }];
    }
    // KOMITE PUSAT - Organization oversight
    if (role === 'komite_pusat') {
      return [{
        name: 'Dasbor',
        icon: LayoutDashboard,
        path: '/committee/dashboard'
      }, {
        name: 'Chapter',
        icon: Users,
        path: '/chapter'
      }, {
        name: 'Event',
        icon: Calendar,
        path: '/committee/events/approvals',
        badge: '3'
      }, {
        name: 'Kebijakan',
        icon: FileText,
        path: '/committee/policies'
      }, {
        name: 'Laporan',
        icon: BarChart,
        path: '/committee/reports'
      }];
    }
    // KOORDINATOR ACARA - Event management
    if (role === 'koordinator_acara') {
      return [{
        name: 'Dasbor',
        icon: LayoutDashboard,
        path: '/events/manage'
      }, {
        name: 'Acara',
        icon: Calendar,
        path: '/events'
      }, {
        name: 'Pendaftaran',
        icon: Users,
        path: '/events/registrations'
      }, {
        name: 'Sertifikat',
        icon: Award,
        path: '/events/certificates'
      }, {
        name: 'Laporan',
        icon: FileText,
        path: '/events/reports'
      }];
    }
    // BENDAHARA - Finance management
    if (role === 'bendahara') {
      return [{
        name: 'Dasbor',
        icon: LayoutDashboard,
        path: '/finance/dashboard'
      }, {
        name: 'Pembayaran',
        icon: CreditCard,
        path: '/payment'
      }, {
        name: 'Invoice',
        icon: FileText,
        path: '/finance/invoices'
      }, {
        name: 'Laporan',
        icon: BarChart,
        path: '/finance/reports'
      }];
    }
    // ANGGOTA BADAN USAHA - Company features
    if (role === 'anggota_badan_usaha') {
      return [{
        name: 'Dasbor',
        icon: LayoutDashboard,
        path: '/dashboard'
      }, {
        name: 'Profil',
        icon: User,
        path: '/profile'
      }, {
        name: 'Lowongan',
        icon: Briefcase,
        path: '/company/jobs'
      }, {
        name: 'Acara',
        icon: Calendar,
        path: '/events'
      }, {
        name: 'Manfaat',
        icon: Gift,
        path: '/benefits'
      }, {
        name: 'Pembayaran',
        icon: CreditCard,
        path: '/payment'
      }];
    }
    // ANGGOTA ORGANISASI - Organization features
    if (role === 'anggota_organisasi') {
      return [{
        name: 'Dasbor',
        icon: LayoutDashboard,
        path: '/dashboard'
      }, {
        name: 'Profil',
        icon: User,
        path: '/profile'
      }, {
        name: 'Anggota',
        icon: Users,
        path: '/organization/members'
      }, {
        name: 'Acara',
        icon: Calendar,
        path: '/events'
      }, {
        name: 'Manfaat',
        icon: Gift,
        path: '/benefits'
      }, {
        name: 'Pembayaran',
        icon: CreditCard,
        path: '/payment'
      }];
    }
    // MITRA ASOSIASI - Limited access
    if (role === 'mitra_asosiasi') {
      return [{
        name: 'Dasbor',
        icon: LayoutDashboard,
        path: '/dashboard'
      }, {
        name: 'Profil',
        icon: User,
        path: '/profile'
      }, {
        name: 'Acara',
        icon: Calendar,
        path: '/events'
      }, {
        name: 'Pengumuman',
        icon: Bell,
        path: '/announcements',
        badge: '2'
      }];
    }
    // ANGGOTA INDIVIDU - Standard member features
    return [{
      name: 'Dasbor',
      icon: LayoutDashboard,
      path: '/dashboard'
    }, {
      name: 'Profil',
      icon: User,
      path: '/profile'
    }, {
      name: 'Lowongan',
      icon: Briefcase,
      path: '/jobs'
    }, {
      name: 'Acara',
      icon: Calendar,
      path: '/events',
      badge: '3'
    }, {
      name: 'Manfaat',
      icon: Gift,
      path: '/benefits'
    }, {
      name: 'Chapter',
      icon: Users,
      path: '/chapter'
    }, {
      name: 'Pengetahuan',
      icon: BookOpen,
      path: '/knowledge-center'
    }, {
      name: 'Pembayaran',
      icon: CreditCard,
      path: '/payment'
    }];
  };
  const menuItems = getMenuItems();
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const getRoleBadge = () => {
    switch (user?.role) {
      case 'super_admin':
        return <Badge className="bg-error text-white text-[10px] px-1.5 py-0" data-id="element-251">
            ADMIN
          </Badge>;
      case 'ketua_chapter':
        return <Badge className="bg-atios-primary text-white text-[10px] px-1.5 py-0" data-id="element-252">
            KETUA
          </Badge>;
      case 'ketua_student_chapter':
        return <Badge className="bg-atios-primary text-white text-[10px] px-1.5 py-0" data-id="element-253">
            STUDENT
          </Badge>;
      case 'komite_pusat':
        return <Badge className="bg-warning text-white text-[10px] px-1.5 py-0" data-id="element-254">
            KOMITE
          </Badge>;
      case 'koordinator_acara':
        return <Badge className="bg-success text-white text-[10px] px-1.5 py-0" data-id="element-255">
            ACARA
          </Badge>;
      case 'bendahara':
        return <Badge className="bg-warning text-white text-[10px] px-1.5 py-0" data-id="element-256">
            KEUANGAN
          </Badge>;
      case 'anggota_badan_usaha':
        return <Badge className="bg-neutral-600 text-white text-[10px] px-1.5 py-0" data-id="element-257">
            PERUSAHAAN
          </Badge>;
      case 'anggota_organisasi':
        return <Badge className="bg-neutral-600 text-white text-[10px] px-1.5 py-0" data-id="element-258">
            ORGANISASI
          </Badge>;
      case 'mitra_asosiasi':
        return <Badge className="bg-neutral-500 text-white text-[10px] px-1.5 py-0" data-id="element-259">
            MITRA
          </Badge>;
      default:
        return <Badge className="bg-neutral-400 text-white text-[10px] px-1.5 py-0" data-id="element-260">
            ANGGOTA
          </Badge>;
    }
  };
  const getDefaultRoute = () => {
    switch (user?.role) {
      case 'super_admin':
        return '/admin/dashboard';
      case 'ketua_chapter':
      case 'ketua_student_chapter':
        return '/chapter/dashboard';
      case 'komite_pusat':
        return '/committee/dashboard';
      case 'koordinator_acara':
        return '/events/manage';
      case 'bendahara':
        return '/finance/dashboard';
      default:
        return '/dashboard';
    }
  };
  return <nav className="sticky top-0 z-50 w-full bg-white border-b border-neutral-200 shadow-sm" data-id="element-261">
      <div className="w-full px-3 sm:px-4 lg:px-6" data-id="element-262">
        <div className="flex items-center justify-between h-14" data-id="element-263">
          {/* Logo */}
          <div className="flex-shrink-0" data-id="element-264">
            <Link to={getDefaultRoute()} className="flex items-center" data-id="element-265">
              <img src="https://cdn.magicpatterns.com/uploads/fWeCgNWwEpM2aXTFp4cDwR/ATIOS-logo-forum.png" alt="ATIOS" className="h-7" data-id="element-266" />
            </Link>
          </div>

          {/* Desktop Menu - Compact & Efficient */}
          <div className="hidden lg:block flex-1 max-w-4xl mx-4" data-id="element-267">
            <div className="flex items-center justify-center space-x-1" data-id="element-268">
              {menuItems.map(item => <Link key={item.name} to={item.path} className={cn('px-3 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2', isActive(item.path) ? 'text-atios-primary bg-atios-light' : 'text-neutral-700 hover:text-neutral-900 hover:bg-neutral-100')} data-id="element-269">
                  <item.icon className="w-4 h-4" data-id="element-270" />
                  <span data-id="element-271">{item.name}</span>
                  {item.badge && <Badge variant="error" className="h-5 min-w-5 text-xs px-1" data-id="element-272">
                      {item.badge}
                    </Badge>}
                </Link>)}
            </div>
          </div>

          {/* User Profile - Compact */}
          <div className="hidden lg:flex items-center gap-2" data-id="element-273">
            <div className="relative" data-id="element-274">
              <button onClick={() => setShowUserMenu(!showUserMenu)} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-neutral-100 transition-all" data-id="element-275">
                <div className="h-8 w-8 rounded-full bg-atios-light border border-atios-primary/20 flex items-center justify-center text-atios-primary font-medium text-sm" data-id="element-276">
                  {user?.avatar || 'U'}
                </div>
                <div className="text-left max-w-[160px]" data-id="element-277">
                  <div className="flex items-center gap-1.5" data-id="element-278">
                    <p className="text-sm font-medium text-neutral-900 truncate" data-id="element-279">
                      {user?.nama}
                    </p>
                    {getRoleBadge()}
                  </div>
                </div>
                <ChevronDown className={cn('w-4 h-4 text-neutral-400 transition-transform', showUserMenu && 'rotate-180')} data-id="element-280" />
              </button>

              {showUserMenu && <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowUserMenu(false)} data-id="element-281" />
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg border border-neutral-200 shadow-lg py-1 z-20" data-id="element-282">
                    <Link to="/profile" className="flex items-center px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-100" onClick={() => setShowUserMenu(false)} data-id="element-283">
                      <User className="w-4 h-4 mr-2 text-neutral-500" data-id="element-284" />
                      Profil
                    </Link>
                    <Link to="/settings" className="flex items-center px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-100" onClick={() => setShowUserMenu(false)} data-id="element-285">
                      <Settings className="w-4 h-4 mr-2 text-neutral-500" data-id="element-286" />
                      Pengaturan
                    </Link>
                    <div className="border-t border-neutral-200 my-1" data-id="element-287" />
                    <button onClick={() => {
                  setShowUserMenu(false);
                  handleLogout();
                }} className="w-full text-left flex items-center px-3 py-2 text-sm text-error hover:bg-error-light" data-id="element-288">
                      <LogOut className="w-4 h-4 mr-2" data-id="element-289" />
                      Keluar
                    </button>
                  </div>
                </>}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="flex lg:hidden" data-id="element-290">
            <Button variant="ghost" size="icon" onClick={() => setIsOpen(!isOpen)} className="h-9 w-9" data-id="element-291">
              {isOpen ? <X className="h-5 w-5" data-id="element-292" /> : <Menu className="h-5 w-5" data-id="element-293" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && <div className="lg:hidden bg-white border-t border-neutral-200" data-id="element-294">
          <div className="px-2 pt-2 pb-3 space-y-1" data-id="element-295">
            {menuItems.map(item => <Link key={item.name} to={item.path} className={cn('block px-3 py-2 rounded-md text-sm font-medium flex items-center gap-3', isActive(item.path) ? 'text-atios-primary bg-atios-light' : 'text-neutral-700 hover:text-neutral-900 hover:bg-neutral-100')} onClick={() => setIsOpen(false)} data-id="element-296">
                <item.icon className="w-5 h-5" data-id="element-297" />
                {item.name}
                {item.badge && <Badge variant="error" className="ml-auto h-5 min-w-5 text-xs px-1.5" data-id="element-298">
                    {item.badge}
                  </Badge>}
              </Link>)}

            <div className="border-t border-neutral-200 mt-4 pt-4" data-id="element-299">
              <div className="flex items-center px-3 mb-3" data-id="element-300">
                <div className="h-10 w-10 rounded-full bg-atios-light border border-atios-primary/20 flex items-center justify-center text-atios-primary font-medium" data-id="element-301">
                  {user?.avatar || 'U'}
                </div>
                <div className="ml-3 flex-1" data-id="element-302">
                  <div className="flex items-center gap-2" data-id="element-303">
                    <div className="text-sm font-medium text-neutral-900" data-id="element-304">
                      {user?.nama}
                    </div>
                    {getRoleBadge()}
                  </div>
                  <div className="text-xs text-neutral-500" data-id="element-305">{user?.email}</div>
                </div>
              </div>
              <Link to="/profile" className="flex items-center px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-100 rounded-md mb-1" onClick={() => setIsOpen(false)} data-id="element-306">
                <User className="w-5 h-5 mr-3 text-neutral-500" data-id="element-307" />
                Profil
              </Link>
              <Link to="/settings" className="flex items-center px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-100 rounded-md mb-1" onClick={() => setIsOpen(false)} data-id="element-308">
                <Settings className="w-5 h-5 mr-3 text-neutral-500" data-id="element-309" />
                Pengaturan
              </Link>
              <button className="w-full flex items-center px-3 py-2 text-sm text-error hover:bg-error-light rounded-md" onClick={() => {
            setIsOpen(false);
            handleLogout();
          }} data-id="element-310">
                <LogOut className="w-5 h-5 mr-3" data-id="element-311" />
                Keluar
              </button>
            </div>
          </div>
        </div>}
    </nav>;
}